import { useMemo } from 'react'

import {
  GetBenchmarkComparisonsDocument,
  GetBenchmarkRoleMappingsDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

import { BenchmarkMatchResult } from 'modules/job_catalog/benchmark/types'
import { toDominanceFlag, toNumber } from 'modules/job_catalog/benchmark/lib/parsers'

/**
 * Fetches benchmark (Mercer) salary data for a tree node across ALL provided
 * country codes at once, returning a Map of countryCode → BenchmarkMatchResult.
 *
 * Used by SalariesCard to show per-workplace market position indicators
 * without needing one hook call per country.
 */
export const useBenchmarkDataByCountry = (
  treeNodeUuid: string,
  countryCodes: string[],
  enabled = true
) => {
  // ── Deduplicate and normalize country codes ──
  const upperCountryCodes = useMemo(
    () => [...new Set(countryCodes.filter(Boolean).map((c) => c.toUpperCase()))].sort(),
    [countryCodes]
  )

  const shouldFetch = enabled && upperCountryCodes.length > 0

  // ── Query 1: Resolve treeNodeUuid → externalJobCode ──
  const { data: mappingsData, loading: mappingsLoading } = useQuery(
    GetBenchmarkRoleMappingsDocument,
    {
      variables: { treeNodeUuids: [treeNodeUuid], activeOnly: true },
      skip: !shouldFetch || !treeNodeUuid,
    }
  )

  const activeMapping = useMemo(
    () => mappingsData?.jobCatalog?.benchmarkRoleMappingsConnection?.nodes?.[0] ?? null,
    [mappingsData]
  )

  // ── Query 2: Get benchmark comparison data for ALL country codes at once ──
  const { data: comparisonData, loading: comparisonLoading } = useQuery(
    GetBenchmarkComparisonsDocument,
    {
      variables: { nodeUuids: [treeNodeUuid], countryCodes: upperCountryCodes },
      skip: !shouldFetch || !activeMapping,
    }
  )

  const comparisonByCountry = useMemo(() => {
    const map = new Map<
      string,
      NonNullable<
        typeof comparisonData
      >['jobCatalog']['benchmarkComparisonsConnection']['nodes'][number]
    >()
    const nodes = comparisonData?.jobCatalog?.benchmarkComparisonsConnection?.nodes ?? []
    for (const node of nodes) {
      if (!node?.countryCode) continue
      const normalizedCountryCode = node.countryCode.toUpperCase()
      map.set(normalizedCountryCode, node)
    }
    return map
  }, [comparisonData])

  // ── Build final map: countryCode (lowercase) → BenchmarkMatchResult ──
  const dataByCountry = useMemo(() => {
    const map = new Map<string, BenchmarkMatchResult>()
    if (!enabled) return map

    if (!activeMapping) {
      for (const upperCode of upperCountryCodes) {
        map.set(upperCode.toLowerCase(), { status: 'unmapped', companySalaryRange: null })
      }

      return map
    }

    for (const upperCode of upperCountryCodes) {
      const lowerCode = upperCode.toLowerCase()

      const comparison = comparisonByCountry.get(upperCode) ?? null

      if (!comparison) {
        map.set(lowerCode, { status: 'no_data', companySalaryRange: null })
        continue
      }

      const hasMarketData =
        comparison.marketP50 != null ||
        comparison.marketNumObservations != null ||
        comparison.marketNumOrgs != null ||
        comparison.marketDistinctOrgCount != null
      if (!hasMarketData) {
        map.set(lowerCode, { status: 'no_data', companySalaryRange: null })
        continue
      }

      const orgCount = comparison.marketDistinctOrgCount ?? comparison.marketNumOrgs ?? null
      const hasMarketSalary = comparison.marketP50 != null
      const dominance = String(comparison.marketDominance ?? '').trim()
      const isSuppressedByDominance = dominance === '*' || dominance === '**'

      if (orgCount == null || orgCount < 3 || isSuppressedByDominance || !hasMarketSalary) {
        map.set(lowerCode, { status: 'no_data', companySalaryRange: null })
        continue
      }

      map.set(lowerCode, {
        status: 'matched',
        companySalaryRange: null,
        tgaP25: toNumber(comparison.marketP25),
        tgaP50: toNumber(comparison.marketP50),
        tgaP75: toNumber(comparison.marketP75),
        tgaMean: toNumber(comparison.marketMean),
        orgCount: comparison.marketNumOrgs ?? null,
        obsCount: comparison.marketNumObservations ?? null,
        distinctOrgCount: comparison.marketDistinctOrgCount ?? null,
        dominanceFlag: toDominanceFlag(comparison.marketDominance),
        currency: comparison.marketCurrency,
        year: comparison.marketYear,
        countryCode: comparison.countryCode,
      })
    }

    return map
  }, [activeMapping, comparisonByCountry, enabled, upperCountryCodes])

  return {
    dataByCountry,
    loading: enabled && (mappingsLoading || comparisonLoading),
    activeMappingType: activeMapping?.mappingType ?? null,
  }
}
